import {
  createSessionManager,
  type StorageAdapter,
  type AuthUser as SessionAuthUser,
} from "@ticketon/authentication-session";
import type {
  SessionBridge,
  SessionSnapshot,
  SessionSetPayload,
  IUser,
} from "@ticketon/authentication-core";

const localStorageAdapter: StorageAdapter = {
  getItem: (key: string) => localStorage.getItem(key),
  setItem: (key: string, value: string) => localStorage.setItem(key, value),
  removeItem: (key: string) => localStorage.removeItem(key),
};

export const sessionManager = createSessionManager(localStorageAdapter);

function sessionUserToIUser(user: SessionAuthUser): IUser {
  const storedRoles = user.roles as unknown;
  let roles: Array<{ externalId: string; name: string }> = [];

  if (Array.isArray(storedRoles)) {
    roles = storedRoles.map((role) => {
      if (typeof role === "string") {
        return { externalId: role, name: role };
      }
      if (typeof role === "object" && role !== null && "externalId" in role && "name" in role) {
        return role as { externalId: string; name: string };
      }
      return { externalId: String(role), name: String(role) };
    });
  }

  return {
    email: user.email,
    firstName: user.firstName || "",
    lastName: user.lastName || "",
    externalId: user.externalId,
    phoneCountryCode: (user.phoneCountryCode as string) || "",
    phone: (user.phone as string) || "",
    avatarUrl: (user.avatarUrl as string) || "",
    authProviderType: (user.authProviderType as IUser["authProviderType"]) || "TICKETON",
    roles,
  };
}

function iUserToSessionUser(user: IUser): SessionAuthUser {
  return {
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    externalId: user.externalId,
    phoneCountryCode: user.phoneCountryCode,
    phone: user.phone,
    avatarUrl: user.avatarUrl,
    authProviderType: user.authProviderType,
    roles: user.roles as unknown as string[],
  };
}

export const sessionBridge: SessionBridge = {
  async get(): Promise<SessionSnapshot> {
    const session = sessionManager.getSession();
    if (!session || !sessionManager.isSessionValid(session)) {
      return { user: null, token: null, tokenExpiration: null };
    }
    return {
      user: sessionUserToIUser(session.user),
      token: session.token,
      tokenExpiration: session.tokenExpiration,
    };
  },

  async set(payload: SessionSetPayload): Promise<void> {
    sessionManager.setSession({
      token: payload.token,
      tokenExpiration: payload.tokenExpiration,
      user: iUserToSessionUser(payload.user),
    });
  },

  async clear(): Promise<void> {
    sessionManager.clearSession();
  },
};
